import { useTranslation } from 'react-i18next';
import { useAreasQuery, useIterationsQuery } from '@platform/state/api';
import { useWorkspaceParams } from '@platform/hooks/useWorkspaceParams';
import { formatDate, todayIso } from '@platform/core/lib/format';
import { cx } from '@platform/core/lib/cx';
import { useAppSelector } from '@platform/state/hooks';
import { selectLang } from '@platform/state/selectors';
import { SystemPage } from '../system/SystemPage';
import { currentIteration } from './currentIteration';
import type { ICustPathsPageProps } from './ICustPathsPageProps';
import type { IPathNodeCount } from './paths/IPathNodeCount';
import { PathRow } from './paths/PathRow';
import { usePathEditor } from './paths/usePathEditor';
import { pathRows } from './pathRows';
import { usePathCounts } from './usePathCounts';
import './customize.css';

/**
 * «Шляхи напрямів» / «Шляхи ітерацій» (прототип v4 areas/iterations): дерево з відступами «└», кольорові позначки напрямів, дати й
 * «Поточний» для ітерацій, «Поточна ітерація: 2026 › Q4 › Спринт 21», записи вузла з нащадками (сервер — recordCount) і дії «+ Дочірній»,
 * «Перейменувати / Редагувати», «Видалити» — форма й палітра під рядком; «+ Новий напрям / ітерація» додає дочірній вузол кореня.
 */
export function CustPathsPage({ kind }: ICustPathsPageProps) {
  const { t } = useTranslation();
  const lang = useAppSelector(selectLang);
  const { ws } = useWorkspaceParams();
  const areas = useAreasQuery(ws, { skip: !ws || kind !== 'areas' });
  const iterations = useIterationsQuery(ws, { skip: !ws || kind !== 'iterations' });
  const nodes = (kind === 'areas' ? areas : iterations).data;
  const serverCounts = !!nodes?.length && typeof (nodes[0] as IPathNodeCount).recordCount === 'number';
  const estimate = usePathCounts(ws, kind === 'areas' ? 'area' : 'iteration', serverCounts ? undefined : nodes);
  const editor = usePathEditor(ws, kind);
  const current = kind === 'iterations' && nodes ? currentIteration(nodes, todayIso()) : null;
  const rows = pathRows(nodes ?? []);
  const chain = (id: string | null | undefined): string[] => { const n = nodes?.find((x) => x.id === id); return n && n.parentId ? [...chain(n.parentId), n.name] : []; };
  const dates = (s?: string | null, e?: string | null) => (s && e ? `${formatDate(s, lang)} – ${formatDate(e, lang)}` : '');
  const root = rows[0]?.node;
  const add = root && (
    <button type="button" className="sp-btn sp-btn--primary" disabled={!editor.canEdit} onClick={() => editor.startAdd(root)}>
      {t(kind === 'areas' ? 'sysCust.paths.newArea' : 'sysCust.paths.newIteration')}
    </button>
  );
  return (
    <SystemPage className="cu-gap16 cu-pathspage" title={t(`sysPages.sections.${kind}`)} actions={add}
      sub={<>{t(`sysCust.paths.${kind}Sub`)}{current && <div className="cu-path__now">{t('sysCust.paths.currentPath', { path: chain(current.id).join(' › ') })}</div>}</>}>
      <div className="cu-paths" role="group" aria-label={t(`sysPages.sections.${kind}`)}>
        <div className={cx('cu-pathgrid cu-pathgrid--head', kind === 'areas' ? 'cu-pathgrid--areas' : 'cu-pathgrid--iters')}>
          <span>{t('sysCust.paths.path')}</span>{kind === 'iterations' && <span>{t('sysCust.paths.dates')}</span>}<span>{t('sysCust.paths.records')}</span>
          <span className="ad-right">{t('sysCust.paths.actions')}</span>
        </div>
        {rows.map((row) => (
          <PathRow key={row.node.id} row={row} kind={kind} editor={editor} current={current?.id === row.node.id} dates={dates(row.node.startDate, row.node.endDate)}
            records={serverCounts ? (row.node as IPathNodeCount).recordCount : estimate?.get(row.node.id)}
            parent={nodes?.find((n) => n.id === row.node.parentId)?.name ?? ''} />
        ))}
      </div>
    </SystemPage>
  );
}
